import { graphemeLength, sliceGraphemes } from '@up2stay/up2promo-commons';
import { Input, Textarea } from '@up2stay/up2promo-ui';
import { cn } from '@up2stay/up2promo-ui';
import * as React from 'react';

function charCountEndPadding(maxLength: number): string {
  const counterChars = String(maxLength).length * 2 + 1;
  if (counterChars <= 5) return 'pr-14';
  if (counterChars <= 7) return 'pr-16';
  return 'pr-[5.5rem]';
}

type CharCountFieldProps = {
  valueLength?: number;
  maxLength: number;
  /** Count and clip by grapheme clusters (emoji-safe). Default true. */
  graphemeCount?: boolean;
};

function CharCountBadge({
  valueLength = 0,
  maxLength,
  multiline = false,
}: CharCountFieldProps & { multiline?: boolean }) {
  return (
    <span
      className={cn(
        'pointer-events-none absolute text-xs tabular-nums text-muted-foreground',
        multiline ? 'bottom-2 right-3' : 'inset-y-0 right-3 flex items-center',
      )}
      aria-hidden
    >
      {valueLength}/{maxLength}
    </span>
  );
}

function applyGraphemeLimit(
  value: string,
  maxLength: number,
  enabled: boolean,
): string {
  if (!enabled) return value;
  return sliceGraphemes(value, maxLength);
}

export const InputWithCharCount = React.forwardRef<
  HTMLInputElement,
  React.ComponentProps<typeof Input> & CharCountFieldProps
>(
  (
    {
      className,
      valueLength,
      maxLength,
      graphemeCount = true,
      onChange,
      ...props
    },
    ref,
  ) => {
    const counted =
      valueLength ??
      graphemeLength(String(props.value ?? props.defaultValue ?? ''));
    return (
      <div className="relative">
        <Input
          ref={ref}
          maxLength={graphemeCount ? undefined : maxLength}
          className={cn(charCountEndPadding(maxLength), className)}
          {...props}
          onChange={(event) => {
            if (graphemeCount) {
              const next = applyGraphemeLimit(
                event.target.value,
                maxLength,
                true,
              );
              if (next !== event.target.value) {
                event.target.value = next;
              }
            }
            onChange?.(event);
          }}
        />
        <CharCountBadge valueLength={counted} maxLength={maxLength} />
      </div>
    );
  },
);
InputWithCharCount.displayName = 'InputWithCharCount';

export const TextareaWithCharCount = React.forwardRef<
  HTMLTextAreaElement,
  React.ComponentProps<typeof Textarea> & CharCountFieldProps
>(
  (
    {
      className,
      valueLength,
      maxLength,
      graphemeCount = true,
      onChange,
      ...props
    },
    ref,
  ) => {
    const counted =
      valueLength ??
      graphemeLength(String(props.value ?? props.defaultValue ?? ''));
    return (
      <div className="relative">
        <Textarea
          ref={ref}
          maxLength={graphemeCount ? undefined : maxLength}
          className={cn('pb-7', charCountEndPadding(maxLength), className)}
          {...props}
          onChange={(event) => {
            if (graphemeCount) {
              const next = applyGraphemeLimit(
                event.target.value,
                maxLength,
                true,
              );
              if (next !== event.target.value) {
                event.target.value = next;
              }
            }
            onChange?.(event);
          }}
        />
        <CharCountBadge valueLength={counted} maxLength={maxLength} multiline />
      </div>
    );
  },
);
TextareaWithCharCount.displayName = 'TextareaWithCharCount';
